import type { AiRolePlayEventPayload, AudioEventPayload, EndEventPayload } from "@expert/expert-university";

import {
    NORMAL_WS_CLOSE_CODE,
    useExpertUniversityConfiguration,
    useExpertUniversityStore,
    WsEvents,
} from "@expert/expert-university";
import { trackFullStoryEvent } from "@expert/monitoring";
import { useEffect, useMemo } from "react";
import useWebSocketModule, { ReadyState } from "react-use-websocket";

import { useFeatureFlag } from "@/shared-utils";

// workaround for [useWebSocket failure · Issue #280 · robtaussig/react-use-websocket](https://github.com/robtaussig/react-use-websocket/issues/280)
// oxlint-disable-next-line typescript/no-useless-default-assignment
const { default: useWebSocket = useWebSocketModule } = useWebSocketModule as unknown as {
    default: typeof useWebSocketModule;
};

import { env } from "@/config";
import { isSimulatedVoiceTask, useActiveTask, useAgentSdk } from "@/sdk";
import { useLogger } from "@/sdk/logging/LoggerProvider";
import { useSessionStore } from "@/sdk/sessions/session.store";

export function useExpertUniversityWebsocket() {
    const logger = useLogger({ module: "Expert University Websocket" });
    const {
        eventBus: expertUniversitySdkEventBus,
        setWSFunctions,
        reconnect,
        setReconnect,
    } = useExpertUniversityStore.getState();
    const { taskInSandbox } = useExpertUniversityConfiguration();
    const activeTask = useActiveTask();
    const agentSdk = useAgentSdk();
    const activeExpertUniversityTask = useMemo(() => {
        return isSimulatedVoiceTask(activeTask) ? activeTask : null;
    }, [activeTask]);

    const { profileId, sessionId, partner } = activeExpertUniversityTask ?? {};

    const { data: isInLiveKitPilot, isLoading: isLiveKitFlagLoading } = useFeatureFlag(
        "EWP-isInLiveKitPilot",
        { profile: profileId, partner },
        { isEnabled: !!profileId },
    );
    const onOpen = () => {
        logger.info({ sessionId, profileId }, "Opening websocket connection");
        const websocketUrl = getWebSocket()?.url;
        if (expertUniversitySdkEventBus && websocketUrl) {
            expertUniversitySdkEventBus.emit("websocket-opened", { websocketUrl });
        }
    };

    const onClose = (event: CloseEvent) => {
        const websocketUrl = getWebSocket()?.url;
        if (expertUniversitySdkEventBus && websocketUrl) {
            expertUniversitySdkEventBus.emit("websocket-closed", { websocketUrl });
        }
        // oxlint-disable-next-line typescript/no-misused-spread
        logger.info({ sessionId, profileId, websocketUrl, ...event }, "Websocket connection closed");
        if (event.code !== NORMAL_WS_CLOSE_CODE) {
            setReconnect(true);
        }
    };

    const onMessage = (event: MessageEvent) => {
        const data = JSON.parse(event.data as string) as AiRolePlayEventPayload;
        if (data.event === WsEvents.ERROR || data.event === WsEvents.END) {
            trackFullStoryEvent(`Websocket ${data.event} event received`, {
                data: data.event === WsEvents.AUDIO ? "Audio data omitted" : data,
                sessionId,
                lastEventId: event.lastEventId,
                origin: event.origin,
                eventCategory: data.event,
            });
            logger.info(
                {
                    data: data.event === WsEvents.AUDIO ? "Audio data omitted" : data,
                    sessionId,
                    profileId,
                    lastEventId: event.lastEventId,
                    origin: event.origin,
                    eventCategory: data.event,
                    endReasonType:
                        data.event === WsEvents.END ? (data.eventPayload as EndEventPayload).reasonType : undefined,
                },
                `Received ${data.event} event`,
            );
            if (data.event === WsEvents.END) {
                // If we receive an END event, we should not reconnect and the task should be ended
                if (activeTask) {
                    void agentSdk.hangupCall(activeTask, "Websocket connection lost");
                }
            }
        }

        if (data.event === WsEvents.AUDIO && expertUniversitySdkEventBus) {
            const payload = data.eventPayload as AudioEventPayload;
            expertUniversitySdkEventBus.emit("audio-event-received", { audioBlob: payload.audioBlob });
        }
    };

    const { readyState, sendMessage, getWebSocket } = useWebSocket(
        env.expertUniversityWebsocketUrl ?? null,
        {
            queryParams:
                sessionId && profileId
                    ? { sessionId, CustomerProfile: profileId, reconnecting: `${reconnect}` }
                    : undefined,
            shouldReconnect: (closeEvent) => !!sessionId && !!profileId && closeEvent.code !== NORMAL_WS_CLOSE_CODE,
            reconnectAttempts: 10,
            reconnectInterval: 5000,
            retryOnError: true,
            onOpen,
            onClose,
            onMessage,
            onReconnectStop: () => {
                logger.info({ sessionId, profileId }, "Stopped attempting to websocket");
            },
            onError: (error) => {
                logger.info({ error, sessionId, profileId }, "WebSocket error");
                return;
            },
        },
        !!sessionId && !!profileId && !taskInSandbox && !isLiveKitFlagLoading && !isInLiveKitPilot, // Only connect for classic EU WS (not sandbox / LiveKit / flag still loading)
    );
    useEffect(() => {
        if (readyState === ReadyState.OPEN) {
            setWSFunctions(sendMessage, getWebSocket);
        }
    }, [getWebSocket, readyState, sendMessage, setWSFunctions]);

    useEffect(() => {
        //End any session with non active simulated voice task
        if (!activeExpertUniversityTask && activeTask?.id.includes("test-training")) {
            trackFullStoryEvent(`CallUnexpectedlyEnded`, { sessionId });

            logger.info({ sessionId, profileId }, "Call unexpectedly ended, ending session");

            useSessionStore.getState().endSession(activeTask.id, "CallUnexpectedlyEnded");
        }
        // oxlint-disable-next-line exhaustive-deps
    }, [activeExpertUniversityTask, activeTask, logger, sessionId, profileId]);
}
